import React, { useEffect } from "react";
import { Database, Boxes } from "lucide-react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { cn } from "../../lib/utils";
import { useWorkflowStore } from "../../store/useWorkflowStore";
import FacilitySelector from "./selectors/FacilitySelector";
import { UseCaseSelector } from "./selectors/UseCaseSelector";
import ChecklistSelector from "../ChecklistSelector";

/**
 * WorkflowSourceForm component
 *
 * Form for selecting source data for the workflow
 */
interface WorkflowSourceFormProps {
  onNext: () => void;
}

const WorkflowSourceForm: React.FC<WorkflowSourceFormProps> = ({ onNext }) => {
  const {
    isFormValid,
    setIsFormValid,
    selectedServiceId,
    selectedFacilityId,
    selectedUseCaseId,
    selectedChecklists,
    currentWorkflow,
    includeObjects,
    setIncludeObjects
  } = useWorkflowStore();

  const isOntologyFlow = currentWorkflow?.type === 'ONTOLOGY';
  
  // Update form validity based on all required selections
  useEffect(() => {
    const isValid =
      !!selectedServiceId &&
      !!selectedFacilityId &&
      !!selectedUseCaseId &&
      selectedChecklists.length > 0;

    setIsFormValid(isValid);
  }, [
    selectedServiceId,
    selectedFacilityId,
    selectedUseCaseId,
    selectedChecklists,
    setIsFormValid
  ]);

  return (
    <div>
      <h2 className="text-xl font-semibold text-foreground mb-4">Source</h2>
      <p className="text-muted-foreground mb-6">
        Select the source instances for the migration.
      </p>

      <div className="flex flex-col space-y-4">
        {/* Facility and Use Case selectors in 50/50 layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FacilitySelector className="w-full" />
          <UseCaseSelector className="w-full" />
        </div>

        {isOntologyFlow && (
          <div className="mt-2 grid w-full items-center gap-2">
            <Label>What to migrate</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setIncludeObjects(false)}
                className={cn(
                  "flex items-start gap-3 rounded-lg border p-4 text-left transition-all",
                  !includeObjects
                    ? "border-primary ring-1 ring-primary bg-primary/5"
                    : "border-input hover:bg-muted/40"
                )}
              >
                <div
                  className={cn(
                    "flex h-9 w-9 items-center justify-center rounded-md shrink-0 transition-colors",
                    !includeObjects
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-foreground"
                  )}
                >
                  <Database className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-foreground">Ontology Schema</div>
                  <div className="text-xs text-muted-foreground mt-0.5 leading-snug">
                    ObjectTypes, Properties, Relations, and System Properties
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setIncludeObjects(true)}
                className={cn(
                  "flex items-start gap-3 rounded-lg border p-4 text-left transition-all",
                  includeObjects
                    ? "border-primary ring-1 ring-primary bg-primary/5"
                    : "border-input hover:bg-muted/40"
                )}
              >
                <div
                  className={cn(
                    "flex h-9 w-9 items-center justify-center rounded-md shrink-0 transition-colors",
                    includeObjects
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-foreground"
                  )}
                >
                  <Boxes className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-foreground">Ontology Schema + Objects</div>
                  <div className="text-xs text-muted-foreground mt-0.5 leading-snug">
                    Also brings referenced Objects from instance
                  </div>
                </div>
              </button>
            </div>
          </div>
        )}

        <div className="mt-2 grid w-full items-center gap-1.5">
          <Label htmlFor="source-checklist">Checklists</Label>
          <ChecklistSelector />
        </div>
      </div>

      <div className="mt-8 flex justify-end">
        <Button
          onClick={onNext}
          disabled={!isFormValid}
          variant={isFormValid ? "default" : "outline"}
          className="px-6"
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default WorkflowSourceForm;
